import { createTheme } from '@mui/material/styles';

const brand = {
  blue:     '#1a3adb',
  blueSoft: '#4d61e3',
  darkBlue: '#0f2167',
  dark:     '#1a1a2e',
  red:      '#c41230',
  teal:     '#006b6b',
};

export const createAppTheme = (mode = 'light') => {
  const isDark = mode === 'dark';

  const palette = {
    mode,
    primary:   { main: brand.blue,     light: brand.blueSoft, dark: brand.darkBlue, contrastText: '#fff' },
    secondary: { main: brand.red,      light: '#d94a63', dark: '#8a0c20',     contrastText: '#fff' },
    success:   { main: '#16a34a',      light: '#22c55e', dark: '#15803d' },
    warning:   { main: '#ea580c',      light: '#f97316', dark: '#c2410c' },
    error:     { main: '#dc2626',      light: '#ef4444', dark: '#b91c1c' },
    info:      { main: '#0ea5e9',      light: '#38bdf8', dark: '#0284c7' },
    grey: {
      50:  '#f9fafb', 100: '#f3f4f6', 200: '#e5e7eb', 300: '#d1d5db',
      400: '#9ca3af', 500: '#6b7280', 600: '#4b5563', 700: '#374151',
      800: '#1f2937', 900: '#111827',
    },
    ...(isDark ? {
      background: { default: '#0f172a', paper: '#1e293b' },
      divider:    'rgba(148, 163, 184, 0.16)',
      text:       { primary: '#f1f5f9', secondary: '#94a3b8' },
      action:     { hover: 'rgba(255,255,255,0.06)', selected: 'rgba(255,255,255,0.1)' },
    } : {
      background: { default: '#f5f7fb', paper: '#ffffff' },
      divider:    '#e5e7eb',
      text:       { primary: '#111827', secondary: '#6b7280' },
    }),
  };

  return createTheme({
    palette,
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h4:         { fontWeight: 700, letterSpacing: '-0.5px' },
      h5:         { fontWeight: 700, letterSpacing: '-0.3px' },
      h6:         { fontWeight: 700 },
      subtitle1:  { fontWeight: 600 },
      subtitle2:  { fontWeight: 600 },
      button:     { fontWeight: 600, letterSpacing: 0 },
      body2:      { lineHeight: 1.55 },
    },
    shape: { borderRadius: 12 },
    shadows: [
      'none',
      isDark ? '0 1px 2px rgba(0, 0, 0, 0.35)' : '0 1px 2px rgba(15, 23, 42, 0.06)',
      isDark ? '0 1px 3px rgba(0, 0, 0, 0.40), 0 1px 2px rgba(0, 0, 0, 0.30)'
             : '0 1px 3px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.04)',
      isDark ? '0 4px 6px -1px rgba(0, 0, 0, 0.5), 0 2px 4px -2px rgba(0, 0, 0, 0.4)'
             : '0 4px 6px -1px rgba(15, 23, 42, 0.08), 0 2px 4px -2px rgba(15, 23, 42, 0.04)',
      isDark ? '0 10px 15px -3px rgba(0, 0, 0, 0.5), 0 4px 6px -4px rgba(0, 0, 0, 0.4)'
             : '0 10px 15px -3px rgba(15, 23, 42, 0.08), 0 4px 6px -4px rgba(15, 23, 42, 0.04)',
      isDark ? '0 20px 25px -5px rgba(0, 0, 0, 0.6), 0 8px 10px -6px rgba(0, 0, 0, 0.5)'
             : '0 20px 25px -5px rgba(15, 23, 42, 0.10), 0 8px 10px -6px rgba(15, 23, 42, 0.06)',
      ...Array(19).fill(isDark ? '0 25px 50px -12px rgba(0, 0, 0, 0.7)'
                                : '0 25px 50px -12px rgba(15, 23, 42, 0.18)'),
    ],
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: palette.background.default,
            fontFeatureSettings: '"cv11", "ss01"',
            WebkitFontSmoothing: 'antialiased',
          },
          '.MuiTableContainer-root': { WebkitOverflowScrolling: 'touch' },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { textTransform: 'none', fontWeight: 600, borderRadius: 8, paddingInline: 14, minHeight: 38 },
          sizeSmall: { minHeight: 32, paddingInline: 10 },
          sizeLarge: { minHeight: 46 },
          containedPrimary: {
            background: brand.blue,
            '&:hover': { background: '#1530b8' },
          },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            borderRadius: 14,
            border: `1px solid ${palette.divider}`,
            boxShadow: isDark ? '0 1px 2px rgba(0,0,0,0.3)' : '0 1px 2px rgba(15, 23, 42, 0.04)',
            transition: 'box-shadow 0.2s, transform 0.2s',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { borderRadius: 14, backgroundImage: 'none' },
          outlined: { border: `1px solid ${palette.divider}` },
          elevation0: { boxShadow: 'none' },
          elevation1: { boxShadow: isDark ? '0 1px 2px rgba(0,0,0,0.4)' : '0 1px 2px rgba(15, 23, 42, 0.06)' },
          elevation2: { boxShadow: isDark
            ? '0 1px 3px rgba(0, 0, 0, 0.5), 0 1px 2px rgba(0, 0, 0, 0.4)'
            : '0 1px 3px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.04)' },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 600, borderRadius: 999, height: 24 },
          sizeSmall: { fontSize: 11, height: 22 },
          outlined: { borderWidth: 1 },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: palette.divider },
          head: {
            fontWeight: 700,
            backgroundColor: isDark ? '#0f172a' : '#f9fafb',
            color: isDark ? '#94a3b8' : '#374151',
            fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4,
          },
        },
      },
      MuiTableContainer: { styleOverrides: { root: { borderRadius: 14 } } },
      MuiTextField: { defaultProps: { size: 'small' } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            backgroundColor: isDark ? '#0f172a' : '#fff',
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 44 },
          indicator: { height: 3, borderRadius: '3px 3px 0 0' },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: { textTransform: 'none', fontWeight: 600, fontSize: 13.5, minHeight: 44, letterSpacing: 0 },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { backgroundColor: '#111827', borderRadius: 6, fontSize: 12, padding: '6px 10px' },
          arrow: { color: '#111827' },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: ({ theme: t }) => ({
            borderRadius: 16,
            [t.breakpoints.down('sm')]: {
              margin: 0, maxHeight: '100%', maxWidth: '100%',
              width: '100%', height: '100%', borderRadius: 0,
            },
          }),
        },
      },
      MuiDialogTitle: { styleOverrides: { root: { fontWeight: 700, fontSize: 18 } } },
      MuiAlert: { styleOverrides: { root: { borderRadius: 10, fontWeight: 500 } } },
      MuiLinearProgress: { styleOverrides: { root: { borderRadius: 4, height: 6 } } },
    },
  });
};

// Default light theme for any module that imports the named binding
const theme = createAppTheme('light');
export default theme;
